// src/components/Dashboard/DashboardComponents/TechnicalContext.jsx
// UPGRADED VERSION - CLEAR CHART NARRATIVE
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, TrendingUp, TrendingDown, MinusCircle, Info, AlertCircle } from 'lucide-react';

/**
 * Enhanced technical context with clearer language and better disclaimers
 */

const TechnicalContextPanel = ({ items }) => {
  const { t } = useTranslation();
  if (!items || items.length === 0) return null;

  const getIcon = (strength) => {
    if (strength === 'Very Strong' || strength === 'Strong') {
      return <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />;
    }
    if (strength === 'Weak') {
      return <TrendingDown className="w-4 h-4 text-red-600 dark:text-red-400" />;
    }
    return <MinusCircle className="w-4 h-4 text-gray-500 dark:text-slate-400" />;
  };

  const getStrengthLabel = (strength) => {
    const labels = {
      'Very Strong': t('dashboard.technicalContext.strengthVeryStrong'),
      'Strong': t('dashboard.technicalContext.strengthStrong'),
      'Weak': t('dashboard.technicalContext.strengthWeak'),
      'Neutral': t('dashboard.technicalContext.strengthNeutral')
    };
    return labels[strength] || t('dashboard.technicalContext.strengthDefault');
  };

  const getStrengthColor = (strength) => {
    if (strength === 'Very Strong') {
      return 'bg-green-100 dark:bg-green-500/10 text-green-700 dark:text-green-300 border-green-500/20';
    }
    if (strength === 'Strong') {
      return 'bg-blue-100 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20';
    }
    return 'bg-gray-200 dark:bg-slate-600/20 text-gray-600 dark:text-slate-400 border-gray-300 dark:border-slate-600/30';
  };

  // Add enhanced context to descriptions
  const enhanceDescription = (item) => {
    const parts = [];
    
    // Base description
    parts.push(item.description);
    
    // Add volatility context if available
    if (item.volatility_percentile) {
      if (item.volatility_percentile > 80) {
        parts.push(t('dashboard.technicalContext.volatilityHigher', { percentile: item.volatility_percentile }));
      } else if (item.volatility_percentile < 20) {
        parts.push(t('dashboard.technicalContext.volatilityLower', { percentile: item.volatility_percentile }));
      }
    }

    // Add volume context if available
    if (item.volume_ratio) {
      if (item.volume_ratio > 2.0) {
        parts.push(t('dashboard.technicalContext.volumeHigher', { ratio: item.volume_ratio.toFixed(1) }));
      } else if (item.volume_ratio < 0.5) {
        parts.push(t('dashboard.technicalContext.volumeBelowTypical'));
      }
    }

    // Add price range context if available
    if (item.price_range) {
      parts.push(t('dashboard.technicalContext.priceRangeOverPeriod', { min: item.price_range.min.toFixed(2), max: item.price_range.max.toFixed(2) }));
    }
    
    return parts.join(' ');
  };

  return (
    <div className="bg-cream-100 dark:bg-slate-900/70 border border-cream-300 dark:border-slate-800/60 rounded-xl p-5">
      {/* HEADER */}
      <div className="flex items-center gap-2 mb-2">
        <BarChart3 className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
        <h2 className="text-base font-bold text-gray-900 dark:text-slate-200">
          {t('dashboard.technicalContext.title')}
        </h2>
      </div>

      <div className="mb-4 p-3 bg-cyan-500/5 border border-cyan-500/10 rounded-lg">
        <p className="text-xs text-gray-500 dark:text-slate-400 leading-relaxed flex items-start gap-2">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-cyan-600 dark:text-cyan-400" />
          <span>
            {t('dashboard.technicalContext.intro')}{' '}
            <strong className="text-gray-700 dark:text-slate-300">{t('dashboard.technicalContext.introBold')}</strong>{' '}
            {t('dashboard.technicalContext.introRest')}
          </span>
        </p>
      </div>

      {/* CARDS */}
      <div className="space-y-3">
        {items.map((item) => (
          <div
            key={item.ticker}
            className="p-4 bg-cream-100 dark:bg-slate-800/50 rounded-lg border border-cream-300 dark:border-slate-700/40 hover:border-cream-900/20 dark:hover:border-slate-600/60 transition-colors"
          >
            {/* HEADER ROW */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="font-bold text-sm text-gray-900 dark:text-slate-100">
                  {item.ticker}
                </div>
                {getIcon(item.strength)}
              </div>

              <span className={`text-xs px-2 py-1 rounded font-medium border ${getStrengthColor(item.strength)}`}>
                {getStrengthLabel(item.strength)}
              </span>
            </div>

            {/* OBSERVATION SUMMARY */}
            <div className="mb-3 p-3 bg-cream-100 dark:bg-slate-900/40 rounded border border-cream-300 dark:border-slate-700/30">
              <h4 className="text-xs font-semibold text-gray-500 dark:text-slate-400 mb-1.5 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {t('dashboard.technicalContext.recentObservations')}
              </h4>
              <p className="text-sm text-gray-800 dark:text-slate-200 leading-relaxed">
                {enhanceDescription(item)}
              </p>
            </div>

            {/* HISTORICAL PATTERN CONTEXT (if available) */}
            {item.historical_pattern && (
              <div className="mb-3 p-3 bg-blue-500/5 border border-blue-500/10 rounded">
                <h4 className="text-xs font-semibold text-blue-700 dark:text-blue-300 mb-1.5">
                  {t('dashboard.technicalContext.historicalPatternTitle')}
                </h4>
                <p className="text-xs text-gray-600 dark:text-slate-300 leading-relaxed">
                  {item.historical_pattern}
                </p>
                <p className="text-[10px] text-gray-500 dark:text-slate-500 mt-1">
                  {t('dashboard.technicalContext.historicalPatternNote')}
                </p>
              </div>
            )}

            {/* KEY METRICS */}
            {(item.volatility_percentile || item.volume_ratio || item.price_change) && (
              <div className="grid grid-cols-3 gap-2 mb-3">
                {item.price_change !== undefined && (
                  <div className="p-2 bg-cream-100 dark:bg-slate-900/40 rounded border border-cream-300 dark:border-slate-700/30 min-w-0">
                    <div className="text-[10px] text-gray-500 dark:text-slate-500 mb-0.5">{t('dashboard.technicalContext.priceChangeLabel')}</div>
                    <div className={`text-sm font-semibold truncate ${
                      item.price_change >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                    }`}>
                      {item.price_change >= 0 ? '+' : ''}{item.price_change.toFixed(2)}%
                    </div>
                  </div>
                )}

                {item.volatility_percentile && (
                  <div className="p-2 bg-cream-100 dark:bg-slate-900/40 rounded border border-cream-300 dark:border-slate-700/30 min-w-0">
                    <div className="text-[10px] text-gray-500 dark:text-slate-500 mb-0.5">{t('dashboard.technicalContext.volatilityLabel')}</div>
                    <div className="text-sm font-semibold text-gray-800 dark:text-slate-200 truncate">
                      {t('dashboard.technicalContext.percentileValue', { percentile: item.volatility_percentile })}
                    </div>
                  </div>
                )}

                {item.volume_ratio && (
                  <div className="p-2 bg-cream-100 dark:bg-slate-900/40 rounded border border-cream-300 dark:border-slate-700/30 min-w-0">
                    <div className="text-[10px] text-gray-500 dark:text-slate-500 mb-0.5">{t('dashboard.technicalContext.volumeLabel')}</div>
                    <div className="text-sm font-semibold text-gray-800 dark:text-slate-200 truncate">
                      {t('dashboard.technicalContext.volumeAvgValue', { ratio: item.volume_ratio.toFixed(1) })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* CONTEXT NOTE */}
            <div className="pt-3 border-t border-cream-300 dark:border-slate-700/30">
              <p className="text-[10px] text-gray-500 dark:text-slate-500 leading-relaxed">
                <strong className="text-gray-600 dark:text-slate-400">{t('dashboard.technicalContext.contextLabel')}</strong> {t('dashboard.technicalContext.contextNote')}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* PANEL DISCLAIMER */}
      <div className="mt-4 p-3 bg-cream-100 dark:bg-slate-800/30 border border-cream-300 dark:border-slate-700/40 rounded-lg">
        <p className="text-[11px] text-gray-500 dark:text-slate-500 text-center leading-relaxed">
          <strong className="text-gray-600 dark:text-slate-400">{t('dashboard.technicalContext.importantLabel')}</strong> {t('dashboard.technicalContext.panelDisclaimer')}
        </p>
      </div>
    </div>
  );
};

export default TechnicalContextPanel;